import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const fmtK = (v) => {
  if (v == null) return "—";
  const a = Math.abs(Math.round(v));
  const s = a >= 1000000 ? `${(a / 1000000).toFixed(2)}M` : a >= 1000 ? `${(a / 1000).toFixed(0)}K` : `${a}`;
  return `${v < 0 ? "-" : ""}$${s}`;
};

const COLORS = ["#94a3b8", "#64748b", "#f59e0b", "#8b5cf6", "#ec4899", "#007BFF"];

export function BacklogHistoryDialog({ open, onClose }) {
  const [snaps, setSnaps] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.getBacklogSnapshots()
      .then(setSnaps)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const shown = snaps.slice(-6);
  const allKeys = [...new Set(shown.flatMap((s) => Object.keys(s.monthly || {})))].sort();
  const chartData = allKeys.map((k) => {
    const [y, m] = k.split("-");
    const row = { name: new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" }) };
    shown.forEach((s) => { row[s.day] = Math.round((s.monthly || {})[k] || 0); });
    return row;
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-4xl" data-testid="backlog-history-modal">
        <DialogHeader>
          <DialogTitle>Backlog History</DialogTitle>
          <p className="text-xs text-slate-500">Each upload is snapshotted (one per day) so you can compare how the forecasted backlog revenue curve shifts over time.</p>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
        ) : snaps.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400" data-testid="history-empty">No snapshots yet — upload a backlog report to start the history</div>
        ) : (
          <div className="space-y-4">
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                <YAxis tickFormatter={fmtK} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} width={54} />
                <Tooltip formatter={(v) => `$${Math.round(v).toLocaleString("en-US")}`} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {shown.map((s, i) => {
                  const isLatest = i === shown.length - 1;
                  return (
                    <Line key={s.day} type="monotone" dataKey={s.day}
                      stroke={isLatest ? "#007BFF" : COLORS[i % COLORS.length]}
                      strokeWidth={isLatest ? 2.5 : 1.5}
                      strokeDasharray={isLatest ? undefined : "4 3"}
                      dot={false} />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-xs" data-testid="history-table">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2">Uploaded</th>
                    <th className="px-3 py-2">File</th>
                    <th className="px-3 py-2 text-right">Lines</th>
                    <th className="px-3 py-2 text-right">Projects</th>
                    <th className="px-3 py-2 text-right">Total Backlog</th>
                    <th className="px-3 py-2 text-right">Δ vs Previous</th>
                  </tr>
                </thead>
                <tbody>
                  {[...snaps].reverse().map((s, i, arr) => {
                    const prev = arr[i + 1];
                    const delta = prev ? s.total_remaining - prev.total_remaining : null;
                    return (
                      <tr key={s.day} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-medium text-slate-700">
                          {new Date(s.uploaded_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          {i === 0 && <span className="ml-1.5 text-[9px] font-semibold text-[#007BFF] uppercase">current</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-500 truncate max-w-[200px]">{s.filename}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{s.line_count.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{s.project_count}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">{fmtK(s.total_remaining)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${delta == null ? "text-slate-300" : delta >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {delta == null ? "—" : `${delta >= 0 ? "+" : ""}${fmtK(delta)}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
